import { useCallback, useMemo, useState, type FormEvent } from "react";
import { CheckCircle2, KeyRound, MonitorSmartphone, ShieldAlert, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { RemotePageState } from "../components/RemotePageState";
import { useRemote } from "../lib/useRemote";
import { AiExplainError } from "../components/AssistantInlineHelp";
import { V3PageTabs, type V3PageTab } from "../components/V3PageTabs";

type Setup = { challengeLabel?: string; setupToken?: string; qrCodeDataUrl?: string; uri?: string; recovery?: string; statusLabel?: string };
type AccountSecurityResponse = {
  password?: { lastChangedAt?: string | null; strengthLabel?: string | null; recovery?: string | null };
  mfa?: { enabled?: boolean; enrolledAt?: string | null; recoveryCodesRemaining?: number | null };
  sessions?: Array<{ id?: string | null; deviceLabel?: string | null; locationLabel?: string | null; lastActiveAt?: string | null; current?: boolean; risk?: string | null }>;
  loginHistory?: Array<{ at?: string | null; deviceLabel?: string | null; locationLabel?: string | null; result?: string | null; risk?: string | null }>;
  trustedDevices?: Array<{ id?: string | null; handle?: string | null; label?: string | null; addedAt?: string | null; lastUsedAt?: string | null; selectionToken?: string | null }>;
};
type AccountSecurityCenterResponse = { mfa: NonNullable<AccountSecurityResponse["mfa"]> };
type ActiveSessionListResponse = { items: Array<{ sessionLabel?: string | null; deviceLabel?: string | null; ipLocationLabel?: string | null; lastSeenAt?: string | null; current?: boolean }> };
type LoginHistoryListResponse = { items: Array<{ occurredAt?: string | null; deviceLabel?: string | null; ipLocationLabel?: string | null; result?: string | null }> };
type TrustedDeviceListResponse = { items: Array<{ deviceLabel?: string | null; lastUsedAt?: string | null; revokeToken?: string | null }> };
type SecurityTask = { key: string; title: string; status: string; detail: string; recovery: string };
type SessionRow = { key: string; device: string; location: string; activeAt: string; status: string; current: boolean };
type HistoryRow = { key: string; at: string; device: string; location: string; result: string; risk: string };
type DeviceRow = { key: string; label: string; addedAt: string; lastUsedAt: string; handle: string };
type PasswordForm = { currentPassword: string; newPassword: string; confirmPassword: string };
type SecurityTab = "settings" | "sessions" | "history";

const securityTabs: V3PageTab[] = [
  { value: "settings", label: "安全设置", icon: KeyRound, panelId: "security-panel-settings" },
  { value: "sessions", label: "会话与设备", icon: MonitorSmartphone, panelId: "security-panel-sessions" },
  { value: "history", label: "登录记录", icon: ShieldAlert, panelId: "security-panel-history" },
];

function createRequestKey(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function stringOf(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function safeText(value: unknown, fallback: string) {
  const text = stringOf(value);
  if (!text) return fallback;
  return text.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, "所选对象").slice(0, 180);
}

function formatSecurityTime(value: unknown, fallback = "尚无记录") {
  if (typeof value !== "string" || !value.trim()) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "时间待确认";
  return date.toLocaleString("zh-CN");
}

function riskBadge(value: string) {
  if (value === "high") return "failed";
  if (value === "normal" || value === "low") return "healthy";
  return "pending";
}

function resultLabel(value: string) {
  if (value === "success") return "成功";
  if (value === "blocked") return "已阻断";
  if (value === "failed") return "失败";
  return "结果待确认";
}

function normalizeSecurity(data: AccountSecurityResponse) {
  const sessions = (data.sessions ?? []).map((item, index): SessionRow => ({
    key: `${safeText(item.deviceLabel, "设备")}-${safeText(item.locationLabel, "位置")}-${item.lastActiveAt ?? index}`,
    device: safeText(item.deviceLabel, "设备待确认"),
    location: safeText(item.locationLabel, "位置待确认"),
    activeAt: formatSecurityTime(item.lastActiveAt),
    status: safeText(item.risk, "normal"),
    current: Boolean(item.current),
  }));
  const loginHistory = (data.loginHistory ?? []).map((item, index): HistoryRow => ({
    key: `${item.at ?? index}-${safeText(item.deviceLabel, "设备")}`,
    at: formatSecurityTime(item.at),
    device: safeText(item.deviceLabel, "设备待确认"),
    location: safeText(item.locationLabel, "位置待确认"),
    result: safeText(item.result, "unknown"),
    risk: safeText(item.risk, "normal"),
  }));
  const trustedDevices = (data.trustedDevices ?? []).map((item, index): DeviceRow => {
    const handle = stringOf(item.selectionToken ?? item.handle ?? item.id);
    return {
      key: `${safeText(item.label, "设备")}-${item.addedAt ?? index}`,
      label: safeText(item.label, "设备待确认"),
      addedAt: formatSecurityTime(item.addedAt),
      lastUsedAt: formatSecurityTime(item.lastUsedAt),
      handle,
    };
  });
  const passwordTime = formatSecurityTime(data.password?.lastChangedAt);
  const mfaEnabled = Boolean(data.mfa?.enabled);
  const tasks: SecurityTask[] = [
    { key: "password", title: "修改密码", status: data.password?.strengthLabel ? "healthy" : "pending", detail: `当前强度：${safeText(data.password?.strengthLabel, "等待平台检查")} · 最近修改：${passwordTime}`, recovery: safeText(data.password?.recovery, "如无法登录，请联系安全管理员重置。") },
    { key: "mfa", title: "双重验证", status: mfaEnabled ? "healthy" : "pending", detail: mfaEnabled ? `已启用，备用码剩余 ${data.mfa?.recoveryCodesRemaining ?? 0} 组` : "尚未启用，建议立即配置。", recovery: "丢失验证器时，请由安全管理员核验身份后恢复。" },
    { key: "sessions", title: "活跃会话", status: sessions.some((item) => item.status === "high") ? "failed" : "healthy", detail: `${sessions.length} 个会话正在记录；异常会话会标记为高风险。`, recovery: "可退出其他会话，当前会话保留。" },
    { key: "history", title: "登录历史", status: loginHistory.some((item) => item.result !== "success") ? "pending" : "healthy", detail: `${loginHistory.length} 条登录记录；用于复核异常登录提醒。`, recovery: "发现陌生设备后，先改密码并退出其他会话。" },
    { key: "devices", title: "可信设备", status: trustedDevices.length ? "healthy" : "unchecked", detail: `${trustedDevices.length} 台设备在可信列表。`, recovery: "移除不用设备后，下一次登录需要重新验证。" },
  ];
  return { tasks, sessions, loginHistory, trustedDevices };
}

export function SecurityPage() {
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [requestKey] = useState(() => createRequestKey("account-security"));
  const [sessionBusy, setSessionBusy] = useState(false);
  const [sessionFeedback, setSessionFeedback] = useState("");
  const [historyFilter, setHistoryFilter] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [deviceBusy, setDeviceBusy] = useState("");
  const [confirmSessions, setConfirmSessions] = useState(false);
  const [deviceTarget, setDeviceTarget] = useState<DeviceRow | null>(null);
  const [activeTab, setActiveTab] = useState<SecurityTab>("settings");
  const loader = useCallback(async () => {
    const [center, sessions, loginHistory, trustedDevices] = await Promise.all([
      api<AccountSecurityCenterResponse>("/api/account-security"),
      api<ActiveSessionListResponse>("/api/account-security/sessions"),
      api<LoginHistoryListResponse>("/api/account-security/login-history"),
      api<TrustedDeviceListResponse>("/api/account-security/trusted-devices"),
    ]);
    const query = historyFilter.trim().toLocaleLowerCase("zh-CN");
    const historyItems = query
      ? loginHistory.items.filter((item) => [item.deviceLabel, item.ipLocationLabel, item.result].some((value) => String(value ?? "").toLocaleLowerCase("zh-CN").includes(query)))
      : loginHistory.items;
    return normalizeSecurity({
      mfa: center.mfa,
      sessions: sessions.items.map((item) => ({
        deviceLabel: item.deviceLabel ?? null,
        locationLabel: item.ipLocationLabel ?? null,
        lastActiveAt: item.lastSeenAt ?? null,
        current: Boolean(item.current),
        risk: "normal",
      })),
      loginHistory: historyItems.map((item) => ({
        at: item.occurredAt ?? null,
        deviceLabel: item.deviceLabel ?? null,
        locationLabel: item.ipLocationLabel ?? null,
        result: item.result ?? null,
        risk: item.result === "success" ? "normal" : "high",
      })),
      trustedDevices: trustedDevices.items.map((item) => ({
        label: item.deviceLabel ?? null,
        lastUsedAt: item.lastUsedAt ?? null,
        selectionToken: item.revokeToken ?? null,
      })),
    });
  }, [historyFilter]);
  const remote = useRemote(loader, (value) => value.tasks.length === 0);

  const taskColumns = useMemo<Array<DataTableColumn<SecurityTask>>>(() => [
    { key: "task", header: "安全项", width: "20%", render: (item) => <strong>{item.title}</strong> },
    { key: "status", header: "状态", width: "18%", render: (item) => <StatusBadge value={item.status} /> },
    { key: "detail", header: "当前情况", width: "34%", render: (item) => item.detail },
    { key: "recovery", header: "恢复方式", width: "28%", render: (item) => item.recovery },
  ], []);
  const sessionColumns = useMemo<Array<DataTableColumn<SessionRow>>>(() => [
    { key: "device", header: "设备", width: "28%", render: (item) => <span className="security-person-cell"><strong>{item.device}</strong><small>{item.current ? "当前会话" : "其他会话"}</small></span> },
    { key: "location", header: "位置", width: "20%", render: (item) => item.location },
    { key: "time", header: "最近活跃", width: "28%", render: (item) => <span className="app-number">{item.activeAt}</span> },
    { key: "risk", header: "风险", width: "24%", render: (item) => <StatusBadge value={riskBadge(item.status)} /> },
  ], []);
  const historyColumns = useMemo<Array<DataTableColumn<HistoryRow>>>(() => [
    { key: "time", header: "时间", width: "22%", render: (item) => <span className="app-number">{item.at}</span> },
    { key: "device", header: "设备", width: "28%", render: (item) => item.device },
    { key: "location", header: "位置", width: "22%", render: (item) => item.location },
    { key: "result", header: "结果", width: "14%", render: (item) => resultLabel(item.result) },
    { key: "risk", header: "风险", width: "14%", render: (item) => <StatusBadge value={riskBadge(item.risk)} /> },
  ], []);
  const deviceColumns = useMemo<Array<DataTableColumn<DeviceRow>>>(() => [
    { key: "label", header: "可信设备", width: "34%", render: (item) => <strong>{item.label}</strong> },
    { key: "added", header: "加入时间", width: "33%", render: (item) => <span className="app-number">{item.addedAt}</span> },
    { key: "used", header: "最近使用", width: "33%", render: (item) => <span className="app-number">{item.lastUsedAt}</span> },
  ], []);

  async function begin() {
    if (mfaBusy) return;
    setMfaBusy(true);
    setError("");
    setDone(false);
    try {
      setSetup(await api<Setup>("/api/account-security/mfa/setup", { method: "POST", body: JSON.stringify({ idempotencyKey: requestKey }) }));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "双重验证设置失败。");
    } finally {
      setMfaBusy(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      await api("/api/account-security/mfa/confirm", { method: "POST", body: JSON.stringify({ code, setupToken: setup?.setupToken, idempotencyKey: requestKey }) });
      setDone(true);
      setSetup(null);
      setCode("");
      await remote.reload();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "双重验证校验失败，输入已保留。");
    }
  }

  async function revokeOtherSessions() {
    if (sessionBusy) return;
    setSessionBusy(true);
    setSessionFeedback("");
    try {
      await api("/api/account-security/sessions/revoke-others", {
        method: "POST",
        body: JSON.stringify({ reason: "本人确认退出其他会话。", idempotencyKey: requestKey }),
      });
      setSessionFeedback("其他会话已退出，当前会话保留。");
      setConfirmSessions(false);
      await remote.reload();
    } catch (failure) {
      setSessionFeedback(failure instanceof Error ? failure.message : "退出其他会话失败，请重试。");
    } finally {
      setSessionBusy(false);
    }
  }

  async function changePassword(form: PasswordForm, idempotencyKey: string) {
    await api("/api/account-security/password", { method: "POST", body: JSON.stringify({ currentPassword: form.currentPassword, newPassword: form.newPassword, idempotencyKey }) });
    setSessionFeedback("密码已更新，后续登录会使用新密码。");
    await remote.reload();
  }

  async function mfaAction(kind: "recovery-codes" | "disable") {
    if (mfaBusy) return;
    setMfaBusy(true);
    setSessionFeedback("");
    try {
      await api(`/api/account-security/mfa/${kind}`, { method: "POST", body: JSON.stringify({ reason: kind === "disable" ? "本人确认停用双重验证。" : "本人生成新的恢复码。", idempotencyKey: createRequestKey(`account-mfa-${kind}`) }) });
      setSessionFeedback(kind === "disable" ? "双重验证停用申请已提交，以平台核验结果为准。" : "恢复码已重新生成，请按平台提示安全保存。");
      await remote.reload();
    } catch (failure) {
      setSessionFeedback(failure instanceof Error ? failure.message : "双重验证操作失败，请重试。");
    } finally {
      setMfaBusy(false);
    }
  }

  async function removeTrustedDevice(item: DeviceRow) {
    if (!item.handle || deviceBusy) return;
    setDeviceBusy(item.key);
    setSessionFeedback("");
    try {
      await api(`/api/account-security/devices/${encodeURIComponent(item.handle)}`, { method: "DELETE", body: JSON.stringify({ reason: "本人移除不再使用的可信设备。", idempotencyKey: createRequestKey("trusted-device-revoke") }) });
      setSessionFeedback("可信设备已移除。");
      setDeviceTarget(null);
      await remote.reload();
    } catch (failure) {
      setSessionFeedback(failure instanceof Error ? failure.message : "可信设备移除失败，请重试。");
    } finally {
      setDeviceBusy("");
    }
  }

  return <div className="page">
    <PageHeader
      icon={ShieldCheck}
      title="账号安全"
      meta={<span>密码、双重验证、会话与设备集中管理</span>}
      navigation={<V3PageTabs tabs={securityTabs} active={activeTab} onChange={(value) => setActiveTab(value as SecurityTab)} ariaLabel="账号安全页内视图" />}
    />
    {sessionFeedback && <div className="security-risk-note" role="status">{sessionFeedback}</div>}
    <RemotePageState state={remote.state} onRetry={remote.reload} emptyHint="尚未返回账号安全状态。">
      {(data) => <div className="workspace-stack">
        <div id="security-panel-settings" role="tabpanel" aria-labelledby="v3-page-tab-settings" className="security-tab-panel workspace-stack" hidden={activeTab !== "settings"}>
          <div className="security-settings-grid">
            <SectionCard icon={KeyRound} title="修改密码" className="security-action-card security-password-card" description="填写当前密码并确认新密码；失败会保留输入。">
              <PasswordPanel onSubmit={changePassword} />
            </SectionCard>
            <SectionCard
            icon={ShieldCheck}
            title="双重验证"
            className="security-action-card security-mfa-card"
            description="使用身份验证器绑定动态验证码；真实启用以平台确认结果为准。"
            action={<div className="inline-actions"><button type="button" className="button secondary" disabled={mfaBusy} onClick={() => void mfaAction("recovery-codes")}>生成恢复码</button><button type="button" className="button ghost" disabled={mfaBusy} onClick={() => void mfaAction("disable")}>受保护停用</button></div>}
          >
            {done && <div className="success-message"><CheckCircle2 />双重验证已确认，下次登录必须输入动态验证码。</div>}
            {error && <div className="form-error" role="alert">{error}</div>}
            {!setup ? <div className="security-mfa-overview">
              <div><strong>使用认证器保护账号</strong><p>按平台提示完成绑定，确认前不会标记为启用。</p></div>
              <button className="button primary" onClick={() => void begin()} disabled={mfaBusy}>{mfaBusy ? "正在准备…" : "启用双重验证"}</button>
            </div> : <form className="mfa-setup" onSubmit={verify}>
              <div className="mfa-enrollment">
                <div className="mfa-qr">
                  <span>{safeText(setup.challengeLabel, "请使用认证器扫码")}</span>
                  {setup.qrCodeDataUrl || setup.uri ? <img src={setup.qrCodeDataUrl ?? setup.uri} alt="双重验证配置二维码" /> : <div className="mfa-qr-loading">等待平台返回配置图</div>}
                  <small>{safeText(setup.recovery, "确认后将生成恢复码。")}</small>
                </div>
                <div className="mfa-manual">
                  <p>输入手机上显示的 6 位动态验证码。</p>
                  <label>验证码<input name="mfaCode" aria-label="验证码" placeholder="验证码" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" required /></label>
                  <button className="button primary">确认启用</button>
                </div>
              </div>
            </form>}
            </SectionCard>
          </div>
          <SectionCard icon={KeyRound} title="账号安全中心" meta={<span>以平台返回结果为准</span>} variant="ledger">
            <DataTable ariaLabel="账号安全任务" rows={data.tasks} columns={taskColumns} rowKey={(item) => item.key} />
          </SectionCard>
        </div>
        <div id="security-panel-sessions" role="tabpanel" aria-labelledby="v3-page-tab-sessions" className="security-tab-panel workspace-stack" hidden={activeTab !== "sessions"}>
          <SectionCard
            icon={MonitorSmartphone}
            title="活跃会话"
            meta={<span>{data.sessions.length} 个</span>}
            variant="ledger"
            action={<button type="button" className="button secondary" onClick={() => setConfirmSessions(true)} disabled={sessionBusy}>{sessionBusy ? "正在退出…" : "退出其他会话"}</button>}
          >
            <DataTable ariaLabel="活跃会话" rows={data.sessions} columns={sessionColumns} rowKey={(item) => item.key} empty="当前没有活跃会话记录。" />
          </SectionCard>
          <SectionCard icon={ShieldCheck} title="可信设备" meta={<span>{data.trustedDevices.length} 台</span>} variant="ledger">
            <DataTable
              ariaLabel="可信设备"
              rows={data.trustedDevices}
              columns={deviceColumns}
              rowKey={(item) => item.key}
              empty="当前没有可信设备。"
              rowActions={(item) => item.handle
                ? <button type="button" className="button ghost" disabled={deviceBusy === item.key} onClick={() => setDeviceTarget(item)}>{deviceBusy === item.key ? "正在移除…" : "移除设备"}</button>
                : <span className="ops-action-note">等待平台返回移除凭证</span>}
            />
          </SectionCard>
        </div>
        <div id="security-panel-history" role="tabpanel" aria-labelledby="v3-page-tab-history" className="security-tab-panel" hidden={activeTab !== "history"}>
          <SectionCard icon={ShieldAlert} title="登录历史" meta={<span>{data.loginHistory.length} 条</span>} variant="ledger"
            filters={<div className="ui-ledger-filters"><label className="ui-ledger-filter-primary security-history-filter"><SearchIcon /><input className="ui-inline-field" aria-label="筛选登录历史" placeholder="筛选登录历史" value={historyFilter} onChange={(event) => setHistoryFilter(event.target.value)} /></label></div>}>
            <DataTable ariaLabel="登录历史" rows={data.loginHistory} columns={historyColumns} rowKey={(item) => item.key} empty="当前没有登录历史。" />
          </SectionCard>
        </div>
      </div>}
    </RemotePageState>
    {confirmSessions && <ConfirmDialog
      title="退出其他会话"
      description="确认退出除当前浏览器外的其他会话？"
      confirmLabel="确认退出"
      busy={sessionBusy}
      onConfirm={() => void revokeOtherSessions()}
      onClose={() => setConfirmSessions(false)}
    >
      <p>陌生设备会立即失效，当前会话保留，失败时可以重新尝试。</p>
    </ConfirmDialog>}
    {deviceTarget && <ConfirmDialog
      title="移除设备"
      description={`确认移除 ${deviceTarget.label}？`}
      confirmLabel="确认移除"
      busy={deviceBusy === deviceTarget.key}
      onConfirm={() => void removeTrustedDevice(deviceTarget)}
      onClose={() => setDeviceTarget(null)}
    >
      <p>移除后，该设备下一次登录需要重新验证。</p>
    </ConfirmDialog>}
  </div>;
}

function SearchIcon() {
  return <ShieldAlert size={15} aria-hidden />;
}

function PasswordPanel({ onSubmit }: { onSubmit: (form: PasswordForm, idempotencyKey: string) => Promise<void> }) {
  const [form, setForm] = useState<PasswordForm>({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [requestKey] = useState(() => createRequestKey("password-change"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form.currentPassword || form.newPassword.length < 10) { setError("请填写当前密码，并设置至少 10 位的新密码。"); return; }
    if (form.newPassword !== form.confirmPassword) { setError("两次输入的新密码不一致。"); return; }
    setBusy(true);
    setError("");
    try {
      await onSubmit(form, requestKey);
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "密码修改失败，表单内容已保留。");
    } finally {
      setBusy(false);
    }
  }
  return <form className="form-grid security-password-form" onSubmit={submit}>
    <label>当前密码<input name="currentPassword" aria-label="当前密码" placeholder="当前密码" type="password" autoComplete="current-password" value={form.currentPassword} onChange={(event) => setForm({ ...form, currentPassword: event.target.value })} /></label>
    <label>新密码<input name="newPassword" aria-label="新密码" placeholder="新密码" type="password" autoComplete="new-password" value={form.newPassword} onChange={(event) => setForm({ ...form, newPassword: event.target.value })} /></label>
    <label>确认新密码<input name="confirmPassword" aria-label="确认新密码" placeholder="确认新密码" type="password" autoComplete="new-password" value={form.confirmPassword} onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })} /></label>
    {error && <div className="form-error full inline-feedback" role="alert"><span>{error}</span><AiExplainError message={error} context="修改密码" /></div>}
    <div className="form-actions full">
      <button className="button primary" disabled={busy}>{busy ? "正在保存…" : "保存密码"}</button>
    </div>
  </form>;
}
